// Scritte della scheda «Spesa per la vendita» (spec 2026-10-15 §3 e §4). Funzioni pure:
// i numeri arrivano dal backend come stringhe e qui si formattano soltanto, mai si calcolano.

const MESI = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];

function migliaia(intero: string): string {
  return intero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/** Divide la stringa decimale in segno, parte intera e decimali, senza passare da Number. */
function parti(s: string): { neg: boolean; intero: string; dec: string } {
  const t = s.trim();
  const neg = t.startsWith("-");
  const [i, d = ""] = t.replace(/^[-+]/, "").split(".");
  return { neg, intero: i === "" ? "0" : i, dec: d };
}

/** Arrotonda a `n` decimali (mezzo verso l'alto sul valore assoluto) lavorando sulle cifre. */
export function arrotonda(s: string, n: number): { neg: boolean; intero: string; dec: string } {
  const { neg, intero, dec } = parti(s);
  if (dec.length <= n) return { neg, intero, dec: dec.padEnd(n, "0") };
  const su = dec.charCodeAt(n) - 48 >= 5;
  let cifre = BigInt(intero + dec.slice(0, n));
  if (su) cifre += 1n;
  const str = cifre.toString().padStart(n + 1, "0");
  const taglio = str.length - n;
  return { neg, intero: str.slice(0, taglio), dec: str.slice(taglio) };
}

export function num(p: { intero: string; dec: string }): string {
  return migliaia(p.intero) + (p.dec ? "," + p.dec : "");
}

export function euro(s: string): string {
  const p = arrotonda(s, 2);
  const nullo = /^0*$/.test(p.intero + p.dec);
  return `${p.neg && !nullo ? "-" : ""}${num(p)} €`;
}

export function prezzoUnitario(s: string, unita: "kWh" | "Smc"): string {
  const { neg, intero, dec } = parti(s);
  const d = dec.length < 3 ? dec.padEnd(3, "0") : dec;
  return `${neg ? "-" : ""}${num({ intero, dec: d })} €/${unita}`;
}

export function quotaMese(s: string): string {
  return `${euro(s)}/mese`;
}

/** Il riferimento di mercato arriva a 6 decimali: in pagina ne bastano 3 (solo presentazione). */
function a3decimali(s: string): string {
  const p = arrotonda(s, 3);
  return `${p.neg ? "-" : ""}${p.intero}.${p.dec}`;
}

/** Consumo: sempre intero (mezzo in su), anche se il valore del backend porta molti decimali. */
export function kwh(s: string, unita: "kWh" | "Smc" = "kWh"): string {
  const p = arrotonda(s, 0);
  const nullo = /^0*$/.test(p.intero);
  return `${p.neg && !nullo ? "-" : ""}${num(p)} ${unita}`;
}

/** Prezzo dell'offerta: il variabile è uno spread, si scrive «PUN + …» (luce) o «PSV + …» (gas). */
export function prezzoOfferta(prezzo: string, tipo: string | null, commodity: string): string {
  const testo = prezzoUnitario(prezzo, commodity === "gas" ? "Smc" : "kWh");
  if (tipo !== "variabile") return testo;
  return `${commodity === "gas" ? "PSV" : "PUN"} + ${testo}`;
}

export function differenza(s: string): { testo: string; verso: "meno" | "piu" | "pari" } {
  const p = arrotonda(s, 2);
  const nullo = /^0*$/.test(p.intero + p.dec);
  const assoluto = `${num(p)} €`;
  if (nullo) return { testo: `${assoluto} in meno`, verso: "pari" };
  // differenza = offerta - bolletta: negativa vuol dire che l'offerta costa meno
  return p.neg
    ? { testo: `${assoluto} in meno`, verso: "meno" }
    : { testo: `${assoluto} in più`, verso: "piu" };
}

export function meseAnno(aaaaMm: string): string {
  const [a, m] = aaaaMm.split("-");
  const nome = MESI[Number(m) - 1];
  return nome ? `${nome} ${a}` : aaaaMm;
}

/** I mesi della bolletta: «giugno 2026», «giugno-luglio 2026», «dicembre 2025 - gennaio 2026». */
export function mesiBolletta(da: string, a?: string | null): string {
  const inizio = meseAnno(da.slice(0, 7));
  if (!a || a.slice(0, 7) <= da.slice(0, 7)) return inizio;
  const fine = meseAnno(a.slice(0, 7));
  return da.slice(0, 4) === a.slice(0, 4) ? `${inizio.split(" ")[0]}-${fine}` : `${inizio} - ${fine}`;
}

export function consumoAnnuo(valore: string, fonte: string, unita: "kWh" | "Smc" = "kWh"): string {
  const base = `Consumo annuo ${kwh(valore, unita)}`;
  return fonte === "bolletta"
    ? `${base}, dalla bolletta`
    : `${base}, stimato dal periodo della bolletta`;
}

// --- Riquadro «La bolletta» (versione A, scritte approvate da Alfonso il 19/10) ---

export const BOLLETTA_TITOLO = "La bolletta";
export const BOLLETTA_QUESTA = "Questa bolletta";
export const BOLLETTA_ANNO = "In un anno";
export const BOLLETTA_SCRITTO = "scritto in bolletta";
export const PREZZO_TITOLO = "Prezzo dell'energia";
export const TIPO_PREZZO: Record<string, string> = { variabile: "Variabile", fisso: "Fisso" };
export const FASCE: Record<string, string> = {
  F1F2: "dalle 7 alle 23, dal lunedì al sabato",
  F3: "la notte, la domenica e nei festivi",
};
export const CAMBIA_CON_OFFERTA = "Cambia con l'offerta";
export const VENDITA_TITOLO = "Spesa per la vendita";

/** I mesi della bolletta senza l'anno: «gennaio», «gennaio e febbraio», da tre in su «da gennaio a marzo». */
export function mesiPeriodo(da: string, a?: string | null): string | null {
  const inizio = Number(da.slice(5, 7)) - 1, annoDa = Number(da.slice(0, 4));
  if (!MESI[inizio]) return null;
  const fine = a ? Number(a.slice(5, 7)) - 1 : inizio, annoA = a ? Number(a.slice(0, 4)) : annoDa;
  const quanti = (annoA - annoDa) * 12 + fine - inizio + 1;
  if (!MESI[fine] || quanti <= 1) return MESI[inizio];
  if (quanti === 2) return `${MESI[inizio]} e ${MESI[fine]}`;
  return `da ${MESI[inizio]} a ${MESI[fine]}`;
}

/** Un valore del prezzo letto: «PUN + 0,026 €/kWh» per il variabile, «0,1249 €/kWh» per il fisso. Le cifre come
 *  stampate (fino a sei decimali), senza gli zeri in coda. */
export function prezzoLetto(valore: string, unita: string | null, indice: string | null): string {
  const testo = `${cifraVoce(valore, 6)} €/${unita === "smc" ? "Smc" : "kWh"}`;
  return indice ? `${indice} + ${testo}` : testo;
}

/** «sugli» davanti ai numeri che si leggono con la vocale (otto, ottanta, ottocento, undici): «sugli 800 Smc». */
function articoloSu(cifra: string): string {
  const testa = cifra.split(".")[0];
  return testa.startsWith("8") || testa === "11" ? "sugli" : "sui";
}

/** La nota sotto le offerte: cosa è la spesa per la vendita e su quale consumo annuo è calcolata. */
export function notaVendita(consumo: string | null, fonte: string | null, unita: "kWh" | "Smc" = "kWh"): string {
  const base = "Spesa per la vendita: energia e quota fissa del venditore, IVA esclusa";
  if (!consumo || !fonte) return `${base}.`;
  const quanto = kwh(consumo, unita);
  return fonte === "bolletta"
    ? `${base}, ${articoloSu(quanto)} ${quanto} dell'ultimo anno.`
    : `${base}, su ${quanto} all'anno, stimati dal periodo della bolletta.`;
}

const GENERICA_BOLLETTA = "Nessun confronto per questa bolletta.";
const GENERICA_OFFERTA = "Nessun confronto.";

const MOTIVI_BOLLETTA: Record<string, string> = {
  "le righe dello scontrino non danno il totale": "i conti dello scontrino non tornano",
  'i "di cui" non danno la riga': "i conti dello scontrino non tornano",
  'i "di cui" sembrano scambiati': "i conti dello scontrino non tornano",
  "scontrino dell'energia non trovato": "scontrino dell'energia non trovato",
  "scontrino trovato ma non letto": "scontrino dell'energia non trovato",
  "importi negativi o consumo nullo (conguaglio): non confrontabile": "bolletta di conguaglio",
  "periodo della bolletta non letto": "periodo della bolletta non letto",
  "durata della quota fissa e periodo non coerenti": "periodo della bolletta non letto",
};

export function motivoBolletta(motivo: string | null): string {
  const scritta = motivo ? MOTIVI_BOLLETTA[motivo] : undefined;
  return scritta ? `Nessun confronto per questa bolletta: ${scritta}.` : GENERICA_BOLLETTA;
}

// voce + participio concordato: «Capacità non indicata», «Perdite di rete non indicate»
const VOCI: Record<string, string> = {
  dispacciamento: "Dispacciamento non indicato",
  capacita: "Capacità non indicata",
  sbilanciamento: "Sbilanciamento non indicato",
  perdite: "Perdite di rete non indicate",
  altri_corrispettivi: "Altri corrispettivi non indicati",
};

export interface ContestoOfferta {
  commodity?: string;
  /** tipo di prezzo della bolletta (fornitura) */
  tipoBolletta?: string | null;
  /** tipo di prezzo dell'offerta */
  tipoOfferta?: string | null;
  riferimentoMercato?: { indice: string; mese: string; valore: string } | null;
}

function tipoPrezzo(t: string | null | undefined): string | null {
  return t === "fisso" || t === "variabile" ? t : null;
}

export function motivoOfferta(motivo: string | null, ctx: ContestoOfferta = {}): string {
  if (!motivo) return GENERICA_OFFERTA;
  if (motivo === "fisso contro variabile: nessuna cifra") {
    const b = tipoPrezzo(ctx.tipoBolletta);
    const o = tipoPrezzo(ctx.tipoOfferta);
    if (!b || !o) return GENERICA_OFFERTA;
    let testo = `Prezzo ${b} in bolletta, ${o} nell'offerta: nessun confronto in euro.`;
    const r = ctx.riferimentoMercato;
    if (r) {
      const unita = ctx.commodity === "gas" ? "Smc" : "kWh";
      const mese = MESI[Number(r.mese.split("-")[1]) - 1];
      if (mese) testo += ` ${r.indice} di ${mese} ${prezzoUnitario(a3decimali(r.valore), unita)}.`;
    }
    return testo;
  }
  const m = /^manca (.+) nell'offerta$/.exec(motivo);
  if (m && VOCI[m[1]]) return `${VOCI[m[1]]} nell'offerta: nessun confronto.`;
  return GENERICA_OFFERTA;
}

// --- Fase 2, «Voci della bolletta» (spec 2026-10-16 fase 2, scritte approvate da Alfonso il 16/10) ---

export const VOCI_TITOLO = "Voci della bolletta";
/** attesa della seconda fase, nella sezione «Voci della bolletta» (frase di Alfonso, 18/10, al Lei come il QR) */
export const VOCI_IN_CORSO = "Stiamo leggendo attentamente la sua bolletta per poterle segnalare eventuali anomalie di attribuzione dei costi.";
/** la stessa attesa nella striscia in cima: una riga sul telefono (proposta A, 18/10) */
export const VOCI_IN_CORSO_BREVE = "Voci a confronto con i valori ufficiali";
export const VOCI_BOTTONE = "Controlla le voci";
export const VOCI_NESSUNA = "Nessuna voce da segnalare";
export const VOCI_ERRORE = "Controllo non riuscito";
export const VOCI_RIPROVA = "Riprova";
export const VOCI_NON_LEGGIBILE = "Dettaglio non leggibile";
export const VOCI_ASSENTE = "Dettaglio assente";
export const VOCI_NOTA = "Confronto con i valori ARERA e Terna del periodo della bolletta.";

export const GIUDIZIO: Record<"G" | "P" | "N" | "V" | "da_verificare", string> = {
  G: "Gonfiata",
  P: "Potrebbe non esserci",
  N: "Non dovrebbe esserci",
  V: "Valore non indicato",       // approvata da Alfonso il 21/10
  da_verificare: "Da verificare",
};

const UNITA_PREZZO: Record<string, string> = {
  kwh: "€/kWh", smc: "€/Smc", mese: "€/mese", "kw/mese": "€/kW al mese", kw: "€/kW", gg: "€/giorno", giorno: "€/giorno",
  gj: "€/GJ",      // gas in €/GJ senza il potere calorifico per passare a €/Smc
};

// quattro, non tre: l'accisa di legge (0,0227 €/kWh) a tre decimali diventerebbe un altro numero (demo QR 18/10)
const DECIMALI_VOCE = 4;

/** Cifra di un prezzo unitario: i decimali che servono, da 2 a `massimo`. */
export function cifraVoce(s: string, massimo = DECIMALI_VOCE): string {
  const { dec } = parti(s);
  const p = arrotonda(s, Math.min(massimo, Math.max(2, dec.replace(/0+$/, "").length)));
  return `${p.neg ? "-" : ""}${num(p)}`;
}

/** Due cifre messe a confronto: se arrotondate sembrano uguali ma non lo sono, i decimali che servono a vederlo (fino a 6). */
export function cifreVoce(a: string, b: string): [string, string] {
  for (let m = DECIMALI_VOCE; m < 6; m++) {
    const x = cifraVoce(a, m), y = cifraVoce(b, m);
    if (x !== y || Number(a) === Number(b)) return [x, y];
  }
  return [cifraVoce(a, 6), cifraVoce(b, 6)];
}

/** Prezzo unitario di una voce con l'unità della bolletta (`cifra` già scritta, se confrontata con un'altra). */
export function prezzoVoce(s: string, unita: string | null | undefined, cifra = cifraVoce(s)): string {
  if (unita === "%") return `${cifra.replace(/,00$/, "")}%`;   // aliquota IVA: «10%»
  return unita && UNITA_PREZZO[unita] ? `${cifra} ${UNITA_PREZZO[unita]}` : `${cifra} €`;
}

const SIGLE = new Set(["PUN", "PSV", "CCP", "ASOS", "ARIM", "IVA", "ARERA", "CMEM", "PCV", "PED", "DISPBT", "POD", "PDR", "QVD", "BT", "MT",
  // gas (B5): vendita e componenti di rete e oneri come le stampa la bolletta (UG1-UG3 hanno la cifra: già maiuscole)
  "CCR", "CPR", "QOA", "QT", "RS", "RE", "GS", "ST", "VR", "CE"]);

/** Nome di una voce stampato tutto in maiuscolo («DISPACCIAMENTO»): in maiuscola normale, le sigle restano. */
export function nomeVoce(s: string): string {
  if (/\p{Ll}/u.test(s) || !/\p{Lu}{2}/u.test(s)) return s;
  const t = s.toLowerCase()
    .replace(/\p{L}[\p{L}\d]*/gu, (w) => (SIGLE.has(w.toUpperCase()) || /\d/.test(w) ? w.toUpperCase() : w))
    .replace(/\(([^)]*)\)/g, (_, dentro: string) => `(${dentro.toUpperCase()})`);
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Il motivo di un «Non dovrebbe esserci», per voce. */
export function motivoFuoriPosto(voce: string): string | null {
  if (voce === "rete-e-oneri-nello-scontrino") return "Costo della compagnia dentro la spesa per la rete";
  if (voce === "servizi-aggiuntivi") return "Servizio aggiuntivo dentro il totale della bolletta";
  if (voce === "spread") return "Componente della formula senza il suo valore";
  return null;
}

export function contaVoci(n: number): string {
  return n === 1 ? "1 voce da segnalare" : `${n} voci da segnalare`;
}

export function motivoDettaglio(motivo: string | null | undefined): string {
  return motivo === "dettaglio non presente" ? VOCI_ASSENTE : VOCI_NON_LEGGIBILE;
}

// --- Vista estesa «Analisi della bolletta» (scritte della colonna A del mockup, 16/10) ---

export const ESTESA_APRI = "Vedi tutte le voci";
export const ESTESA_TITOLO = "Analisi della bolletta";
export const ESTESA_CHIUDI = "Chiudi";
export const ESTESA_IN_REGOLA = "In regola";
export const ESTESA_PREZZO_LIBERO = "Prezzo libero";
export const ESTESA_NON_CONTROLLATA = "Non controllata";
// esiti e motivi della copertura (spec fase 2 §11, scritte approvate da Alfonso il 17/10, colonna A)
// colonna «Valore ufficiale» per sconti, arrotondamenti, deposito (Alfonso 17/10: «Nessuno» invece di «Non regolata»);
// sul telefono, senza la colonna, la frase intera
export const ESTESA_NON_REGOLATA = "Nessuno";
export const ESTESA_NON_REGOLATA_TELEFONO = "Nessun valore ufficiale";
export const ESTESA_NON_VERIFICABILE = "Non verificabile";
const MOTIVO_RIGA: Record<string, string> = {
  conguaglio: "Conguaglio di bollette precedenti",
  iva_22: "IVA al 22% su una fornitura di casa",
  tipo_cliente: "Tipo di cliente non indicato in bolletta",
  periodo: "Periodo non indicato in bolletta",
};
/** Il motivo scritto sotto una voce; tipo di cliente e periodo solo dove l'avviso in testa non c'è. */
export function motivoRiga(motivo: string | null | undefined, conAvviso = false): string | null {
  if (!motivo || (conAvviso && (motivo === "tipo_cliente" || motivo === "periodo"))) return null;
  return MOTIVO_RIGA[motivo] ?? null;
}
/** L'avviso detto una volta in testa alla vista estesa quando mancano tipo di cliente o periodo. */
export function avvisoMancano(mancano: string[]): string | null {
  const tipo = mancano.includes("tipo_cliente"), periodo = mancano.includes("periodo");
  const cosa = tipo && periodo ? "Tipo di cliente e periodo non indicati" : tipo ? "Tipo di cliente non indicato"
    : periodo ? "Periodo non indicato" : null;
  return cosa && `${cosa} in bolletta: le voci che ne dipendono restano da verificare.`;
}
// --- Gas nella finestra (scritte approvate da Alfonso il 23/10, scritte-gas-approvate.md) ---
// I motivi delle righe del gas (`parte: "gas"`) arrivano dal backend già scritti con le frasi approvate, unite da «; »:
// il backend è la fonte unica di quel testo (costanti MOTIVO_* di gas/calcolo.py e ai/esito_gas.py). Qui si
// separano soltanto. I motivi della luce restano codici, tradotti da `motivoRiga`.
export const IVA_ESCLUSA = "IVA esclusa";
export const AI_ERRORE_GAS = "Analisi del gas non riuscita: sono mostrate solo le voci trovate dai controlli automatici.";
/** Le frasi del motivo di una riga del gas, una per riga; «IVA esclusa» si scrive accanto agli euro, non sotto. */
export function motiviGas(motivo: string | null | undefined): { righe: string[]; ivaEsclusa: boolean } {
  const frasi = (motivo ?? "").split("; ").map((x) => x.trim()).filter(Boolean);
  return { righe: frasi.filter((x) => x !== IVA_ESCLUSA), ivaEsclusa: frasi.includes(IVA_ESCLUSA) };
}
/** Bolletta col gas, luce che il motore non analizza: il motivo già usato per il dettaglio, dopo i due punti. */
export function avvisoLuce(motivo: string | null | undefined): string {
  const m = motivoDettaglio(motivo);
  return `Luce non analizzata: ${m.charAt(0).toLowerCase()}${m.slice(1)}.`;
}
const giornoMeseAnno = (aaaaMmGg: string) => aaaaMmGg.split("-").reverse().join("/");
/** Il periodo stampato diverso da quello del riepilogo (ruling 46), all'operatore. */
export function avvisoPeriodo(p: { da: string; a: string }): string {
  return `Periodo stampato (dal ${giornoMeseAnno(p.da)} al ${giornoMeseAnno(p.a)}) diverso da quello del riepilogo: ` +
    "i valori ufficiali sono del riepilogo.";
}
export const ESTESA_NESSUNA = "Nessuna.";
export const ESTESA_COLONNE = ["Voce", "In bolletta", "Valore ufficiale", "Giudizio"] as const;
export const ESTESA_COLONNE_SENZA_GIUDIZIO = ["Voce", "In bolletta", "Valore ufficiale"] as const;
/** analisi dell'AI (versione B, scelta di Alfonso dalle anteprime del 21/10) */
export const VOCI_DA_SEGNALARE = "Voci da segnalare";
export const NOTE_TITOLO = "Note sulla bolletta";
export const RIGHE_TITOLO = "Righe della bolletta";
export const ESTESA_NOTA = "Valori ufficiali di ARERA e Terna per il periodo della bolletta. Le voci a prezzo libero non hanno un valore ufficiale: si confrontano con l'offerta.";
export const ESTESA_EURO_IN_PIU = "in più in questa bolletta";
/** quanto pesa una voce segnalata (frase già approvata del pannello delle voci) */
export const VOCI_EURO_IN_BOLLETTA = "in questa bolletta";
/** apre la spiegazione breve di una voce segnalata (resa B scelta da Alfonso il 17/10) */
export const VOCI_PERCHE = "Perché";
/** € fatturati oltre la somma della formula del riquadro (scritta scelta da Alfonso il 17/10) */
// «non spiegati dalla formula» non si capiva (Alfonso 19/10)
export const VOCI_NON_SPIEGATI = "oltre la formula scritta in bolletta";
/** la colonna del valore ufficiale, per la riga della formula: la somma della formula più le perdite di rete che il
 *  controllo aggiunge (scelta di Alfonso il 17/10: la riga lo dichiara) */
export function dallaFormula(perdite?: string | null): string {
  return perdite ? `formula + perdite ${Number(perdite).toLocaleString("it-IT")}%` : "dalla formula";
}

/** davanti alla cifra stimata («circa 6,20 € in più in questa bolletta», approvata da Alfonso il 18/10) */
export const CIRCA = "circa";
/** «circa » davanti a una cifra stimata, niente se è esatta */
export function circa(stimato?: boolean | null): string {
  return stimato ? `${CIRCA} ` : "";
}

/** In coda al «Perché» della capacità stimata sulla quota di picco ricavata dalla formula (frase approvata da Alfonso
 *  il 18/10, con la percentuale della bolletta). */
export function percheStimato(quotaPicco: string): string {
  const pct = Number(quotaPicco).toLocaleString("it-IT", { maximumFractionDigits: 1 });
  return `La bolletta non dice quanti kWh sono caduti nelle ore di picco: dalla formula stampata e dal prezzo fatturato risultano circa il ${pct}%. La cifra è una stima.`;
}

/** Il «Perché» di un «Valore non indicato» (frase approvata da Alfonso il 21/10): i nomi tra virgolette basse, uniti
 *  da «, » e da « e » prima dell'ultimo. */
export function percheValoreNonIndicato(mancanti: string[]): string {
  const nomi = mancanti.map((n) => `«${n}»`);
  const elenco = nomi.length > 1 ? `${nomi.slice(0, -1).join(", ")} e ${nomi[nomi.length - 1]}` : nomi[0];
  return `La formula nomina ${elenco}, ma la bolletta non ne scrive il valore: il prezzo non si può ricontrollare. ` +
    "La delibera ARERA 315/2024 chiede di indicarlo nel riquadro dell'offerta.";
}

/** Il testo del «Perché»: gli elementi della formula senza valore, la spiegazione della voce e, se la cifra è stimata,
 *  da dove viene la stima. */
export function testoPerche(descrizione?: string | null, quotaPicco?: string | null, mancanti?: string[] | null): string | null {
  const stima = quotaPicco ? percheStimato(quotaPicco) : null;
  const formula = mancanti?.length ? percheValoreNonIndicato(mancanti) : null;
  return [formula, descrizione, stima].filter(Boolean).join(" ") || null;
}

/** «In breve»: quante voci controllate e da segnalare, e la gonfiata più pesante con i suoi € in più. */
export function inBreve(controllate: number, daSegnalare: number, principale?: { nome?: string; dicitura: string; euro_in_piu: string | null; stimato?: boolean | null } | null): string {
  const voci = controllate === 1 ? "una voce controllata" : `${controllate} voci controllate`;
  const quante = daSegnalare === 0 ? "nessuna da segnalare" : daSegnalare === 1 ? "una da segnalare" : `${daSegnalare} da segnalare`;
  const testa = `Su ${voci}, ${quante}.`;
  if (!principale?.euro_in_piu) return testa;
  return `${testa} ${nomeVoce(principale.nome || principale.dicitura)} gonfiata: ${circa(principale.stimato)}${euro(principale.euro_in_piu)} ${ESTESA_EURO_IN_PIU}.`;
}
